import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { activeAdminTenantId, canWriteTenant, useAuth } from '../../auth/AuthContext';
import { useFormatDateTime } from '../../util/displayTimeZone';
import * as instancesApi from '../../api/procedureInstances';
import {
  getMyCompareScaffold,
  type ParticipantCompareScaffold,
} from '../../api/sessionMetrics';
import {
  getTenant,
  listParticipants,
  type Participant,
} from '../../api/tenants';
import { FocusOverlay } from '../../components/FocusOverlay';
import { CreateCaseDialog } from '../../components/CreateCaseDialog';
import { ParticipantCompareWelcome } from '../../components/ParticipantCompareWelcome';
import { CompareView } from '../../components/CompareView';
import { CohortReportView } from '../../components/cohortReport/CohortReportView';

/**
 * Focus (MULTI-PARTICIPANT-FOCUS) — a top-level destination for comparing
 * EEG focus across the people who worked one case, on a shared wall-clock
 * axis. Pick a case; the overlay shows every team member's focus trace so
 * you can see where their attention diverges and jump to the moments.
 *
 * v1 selects by clinical event (procedure instance) — the unit that groups
 * concurrent recordings. This is deliberately its OWN nav item, not buried
 * under Procedures. The Compare hub (individual/cohort/department modes)
 * folds in here later.
 */
export function TenantFocus() {
  const { user } = useAuth();
  const adminTenantId = activeAdminTenantId(user);
  // No admin/coordinator tenant → participant read-only view (their own
  // cases via /me, no create/edit/delete, media panels hidden).
  const asParticipant = !adminTenantId;
  const tenantId = adminTenantId ?? '';
  const canWrite = !asParticipant && canWriteTenant(user, adminTenantId);
  // A participant may CREATE a case from recordings they were present for
  // (their own plus anyone co-present — the server decides), but not edit or
  // delete one. Editing is the asymmetry that matters: detaching a recording
  // could remove the very participation that grants access to the case, so
  // reshaping an existing case stays staff-only.
  const canCreate = canWrite || asParticipant;
  const fmtDateTime = useFormatDateTime();

  const [cases, setCases] = useState<instancesApi.ProcedureInstance[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [deleting, setDeleting] = useState<instancesApi.ProcedureInstance | null>(null);
  const [busy, setBusy] = useState(false);
  // Cases (team overlay) vs Benchmark (the Compare hub folded in from the
  // dashboard). Benchmark means different things by role: a participant
  // benchmarks their OWN metrics vs the cohort (self-vs-cohort); a tenant
  // admin compares participants head-to-head (the cross-participant Compare
  // that used to live on the admin dashboard).
  const [mode, setMode] = useState<'cases' | 'benchmark' | 'reports'>('cases');
  const [scaffold, setScaffold] = useState<ParticipantCompareScaffold | null>(
    null,
  );
  const [participants, setParticipants] = useState<Participant[] | null>(null);
  const [compareEnabled, setCompareEnabled] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (asParticipant) {
      // Participant: their own self-vs-cohort scaffold.
      getMyCompareScaffold({})
        .then((s) => !cancelled && setScaffold(s))
        .catch(() => !cancelled && setScaffold(null));
    } else if (adminTenantId) {
      // Admin: the cross-participant Compare needs the roster + the tenant's
      // Compare feature flag (hidden when the tenant switched it off).
      listParticipants(adminTenantId)
        .then((p) => !cancelled && setParticipants(p))
        .catch(() => !cancelled && setParticipants([]));
      getTenant(adminTenantId)
        .then(
          (t) =>
            !cancelled && setCompareEnabled(t.compare_enabled_tenant !== false),
        )
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [asParticipant, adminTenantId]);

  // The Benchmark tab is available to every participant, and to an admin only
  // when the tenant has Compare enabled. Fall back to By case if a stale
  // 'benchmark' selection is no longer offered.
  const showBenchmarkTab = asParticipant || compareEnabled;
  // Reports show everyone's names side by side: admins and coordinators only.
  const showReportsTab = !asParticipant;
  const activeMode =
    (mode === 'benchmark' && !showBenchmarkTab) || (mode === 'reports' && !showReportsTab)
      ? 'cases'
      : mode;

  const openNew = () => {
    setEditId(null);
    setDialogOpen(true);
  };
  const openEdit = (id: string) => {
    setEditId(id);
    setDialogOpen(true);
  };

  useEffect(() => {
    let live = true;
    setError(null);
    const load = asParticipant
      ? instancesApi.listMyCases()
      : instancesApi.listInstances(tenantId);
    load
      .then((rows) => {
        if (!live) return;
        setCases(rows);
        setSelectedId((cur) => cur || (rows[0]?.id ?? ''));
      })
      .catch(() => live && setError('Could not load cases.'));
    return () => {
      live = false;
    };
  }, [tenantId, asParticipant, reloadKey]);

  const label = useMemo(
    () => (c: instancesApi.ProcedureInstance) => {
      if (c.external_case_ref) return c.external_case_ref;
      const when = c.started_at ?? c.scheduled_at ?? c.created_at;
      return `${c.procedure_name}${when ? ` · ${fmtDateTime(when)}` : ''}`;
    },
    [fmtDateTime],
  );

  // Secondary line: who's in the case + how many recordings — so a day full of
  // same-procedure cases is tellable apart at a glance. Empty when unknown.
  const subLabel = (c: instancesApi.ProcedureInstance): string => {
    const who = (c.participants ?? []).join(', ');
    const n = c.session_count ?? 0;
    const count = n > 0 ? `${n} recording${n === 1 ? '' : 's'}` : '';
    return [who, count].filter(Boolean).join(' · ');
  };

  if (!tenantId && !asParticipant) {
    return <Alert severity="info">Select a tenant to compare focus.</Alert>;
  }

  const openCase = (id: string) => {
    setDialogOpen(false);
    setSelectedId(id);
    setReloadKey((k) => k + 1);
  };

  const selectedCase = cases?.find((c) => c.id === selectedId) ?? null;
  // Case picker: alphabetical by the primary line; typing matches the
  // secondary line (who took part) too.
  const caseOptions = [...(cases ?? [])].sort((a, b) =>
    label(a).localeCompare(label(b), undefined, { sensitivity: 'base', numeric: true }),
  );
  const filterCases = createFilterOptions<instancesApi.ProcedureInstance>({
    stringify: (c) => `${label(c)} ${subLabel(c)}`,
  });

  async function doDelete() {
    if (!tenantId || !deleting) return;
    setBusy(true);
    try {
      await instancesApi.deleteInstance(tenantId, deleting.id);
      const remaining = (cases ?? []).filter((c) => c.id !== deleting.id);
      setDeleting(null);
      setSelectedId(remaining[0]?.id ?? '');
      setReloadKey((k) => k + 1);
    } catch {
      setError('Could not delete the case.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        className="no-print"
      >
        <Stack>
          <Typography variant="h4">Analysis</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeMode === 'cases'
              ? 'Compare EEG focus across the team on a case, aligned on one ' +
                'wall-clock axis — see where attention diverges and which ' +
                'moments drove it.'
              : activeMode === 'reports'
                ? 'Put the participants of a procedure side by side — who took longer, ' +
                  'whose hands moved more smoothly — and print it as a report.'
              : asParticipant
                ? 'Benchmark your own metrics against the reference cohorts, ' +
                  'procedure by procedure.'
                : 'Compare participants head-to-head on a procedure — against ' +
                  'each other and the reference cohorts.'}
          </Typography>
        </Stack>
        {canCreate && activeMode === 'cases' && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            New case
          </Button>
        )}
      </Stack>

      <Tabs
        value={activeMode}
        onChange={(_e, v: 'cases' | 'benchmark' | 'reports') => setMode(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
        className="no-print"
      >
        <Tab value="cases" label="By case" />
        {showBenchmarkTab && <Tab value="benchmark" label="Benchmark" />}
        {showReportsTab && <Tab value="reports" label="Reports" />}
      </Tabs>

      {activeMode === 'reports' ? (
        <CohortReportView tenantId={tenantId} />
      ) : activeMode === 'benchmark' ? (
        asParticipant ? (
          scaffold === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <ParticipantCompareWelcome
              tenantId={adminTenantId ?? undefined}
              scaffold={scaffold}
            />
          )
        ) : participants === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <CompareView tenantId={adminTenantId ?? ''} participants={participants} />
        )
      ) : (
        <>
      {error && <Alert severity="error">{error}</Alert>}

      {cases === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={26} />
        </Box>
      ) : cases.length === 0 ? (
        <Alert
          severity="info"
          action={
            canCreate && (
              <Button size="small" onClick={openNew}>
                New case
              </Button>
            )
          }
        >
          {asParticipant
            ? 'No cases yet. Build one from recordings you took part in, or you’ll see a case here once one of yours is grouped into one.'
            : 'No cases yet. Tag concurrent recordings into a case to compare the team’s focus across it.'}
        </Alert>
      ) : (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <Autocomplete<instancesApi.ProcedureInstance, false, true, false>
              size="small"
              sx={{ flex: 1, maxWidth: 460 }}
              options={caseOptions}
              // Always set while cases exist (the first one is auto-selected).
              value={selectedCase as instancesApi.ProcedureInstance}
              onChange={(_e, c) => setSelectedId(c.id)}
              disableClearable
              autoHighlight
              // Keep the CLOSED picker compact — primary line only.
              getOptionLabel={(c) => label(c)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={filterCases}
              renderOption={(props, c) => {
                const { key, ...rest } = props as typeof props & { key: string };
                const sub = subLabel(c);
                return (
                  <li key={`${key}-${c.id}`} {...rest}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {label(c)}
                      </Typography>
                      {sub && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {sub}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} label="Case" />}
            />
            {canWrite && selectedCase && (
              <>
                <Tooltip title="Edit case (recordings + name)">
                  <IconButton aria-label="Edit case" onClick={() => openEdit(selectedCase.id)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete case">
                  <IconButton aria-label="Delete case" color="error" onClick={() => setDeleting(selectedCase)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>

          {selectedId && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <FocusOverlay
                tenantId={tenantId}
                instanceId={selectedId}
                asParticipant={asParticipant}
              />
            </Paper>
          )}
        </>
      )}
        </>
      )}

      <CreateCaseDialog
        tenantId={tenantId}
        open={dialogOpen}
        editInstanceId={editId}
        onClose={() => setDialogOpen(false)}
        onSaved={openCase}
        asParticipant={asParticipant}
      />

      {/* Delete */}
      <Dialog open={deleting != null} onClose={() => !busy && setDeleting(null)}>
        <DialogTitle>Delete case?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete “{deleting ? label(deleting) : ''}”? This unlinks its recordings
            from the case — the recordings themselves are not deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={busy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={doDelete} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
